import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { CSVLink } from 'react-csv';
import Header from '../../common/Header/Header';
import {
  Buttons,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import Divider from '@mui/material/Divider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useSizes } from '../../services/size/size.service';
import { Size } from '../../services/size/size.types';
import { useFetchErrorToast } from '../../utils/useFetchErrorToasts/useFetchErrorToast';

const Sizes = () => {
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRow, setSelectedRow] = useState<Size | undefined>(undefined);
  const navigate = useNavigate();
  const muiTheme = createTheme();

  const { data, isError: sizesError } = useSizes(true);

  const allSizes = data?.data || [];

  const headers = [
    { label: 'MOA Size No.', key: 'sizeNo' },
    { label: 'Size No.', key: 'sizeNo' },
    { label: 'Description', key: 'description' },
    { label: 'Status No.', key: 'statusNo' },
    { label: 'Mnemonic', key: 'mnemonic' },
    { label: 'Seq No.', key: 'seqNo' },
    { label: 'Long Description', key: 'longDescription' },
    { label: 'BOH description', key: 'displayDesc' },
    { label: 'Valid on Web', key: 'validOnWeb' },
    { label: 'Valid in Order Entry', key: 'displayInOe' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Updated At', key: 'updatedAt' },
  ];

  const csvData = allSizes.map((size: Size) => ({
    sizeNo: size.sizeNo,
    description: size.description,
    statusNo: size.statusNo,
    mnemonic: size.mnemonic,
    seqNo: size.seqNo,
    longDescription: size.longDescription,
    displayDesc: size.displayDesc,
    validOnWeb: size.validOnWeb ? 'Y' : 'N',
    displayInOe: size.displayInOe ? 'Y' : 'N',
  }));

  const handleRowSelection = () => {
    const selectedRows = gridRef?.current?.api.getSelectedRows();
    setSelectedRow(selectedRows?.[0]);
  };

  const handleOpenClick = () => {
    navigate(`/tables/sizes/${selectedRow?.sizeNo}`);
  };

  useFetchErrorToast([{ isError: sizesError, label: 'Error fetching sizes', id: 'sizesFetchError' }]);

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Size' />
      <Page>
        <TableWrapper>
          <Table
            rowData={data?.data}
            ref={gridRef}
            returnedRecords={data?.data?.length}
            height={'100%'}
            rowSelection='single'
            onRowSelected={handleRowSelection}
            columnDefs={[
              {
                minWidth: 48,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { minWidth: 112, field: 'sizeNo', headerName: 'MOA Size No.' },
              { minWidth: 112, field: 'sizeNo', headerName: 'Size No.' },
              { minWidth: 112, field: 'description', headerName: 'Description' },
              { minWidth: 80, field: 'statusNo', headerName: 'Status No.' },
              { minWidth: 80, field: 'mnemonic', headerName: 'Mnemonic' },
              { minWidth: 80, field: 'seqNo', headerName: 'Seq No.' },
              { minWidth: 180, field: 'longDescription', headerName: 'Long Description' },
              { minWidth: 112, field: 'displayDesc', headerName: 'BOH description' },
              {
                minWidth: 112,
                field: 'validOnWeb',
                headerName: 'Valid on Web',
                valueFormatter: (params) => (params.value === false ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (params.getValue('validOnWeb') === false ? 'N' : 'Y'),
              },
              {
                minWidth: 112,
                field: 'displayInOe',
                headerName: 'Valid in Order Entry',
                valueFormatter: (params) => (params.value === false ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) =>
                  params.getValue('displayInOe') === false ? 'N' : 'Y',
              },
            ]}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label='Open'
            variant={Variant.Primary}
            onClick={handleOpenClick}
            disabled={selectedRow === undefined}
          />
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
          <CSVLink
            data={csvData}
            headers={headers}
            filename={`Sizes${new Date().toISOString().split('T')[0]}.csv`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Button variant={Variant.Secondary} label='Export to CSV' />
          </CSVLink>
        </Buttons>
      </Page>
    </>
  );
};

export default Sizes;
